// src/components/shared/LoadingFallback.js
// Lightweight loading component for React.lazy() Suspense fallback

import React from "react";
import { useTranslation } from "react-i18next";

/**
 * Loading fallback component for code-split routes
 * Displays a minimal, fast-loading spinner while chunks are being fetched
 */
const LoadingFallback = ({ message }) => {
  const { t } = useTranslation();
  const displayMessage = message || t("loading.default", "Loading...");
  return (
    <div
      role="status"
      aria-label={displayMessage}
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "50vh",
        padding: "2rem",
      }}
    >
      {/* CSS-only spinner for fast initial render */}
      <div
        aria-hidden="true"
        style={{
          width: "40px",
          height: "40px",
          border: "3px solid #f3f3f3",
          borderTop: "3px solid #3B82F6",
          borderRadius: "50%",
          animation: "spin 0.8s linear infinite",
        }}
      />
      <p
        style={{
          marginTop: "1rem",
          color: "#6B7280",
          fontSize: "0.875rem",
          fontFamily: "'Poppins', sans-serif",
        }}
      >
        {displayMessage}
      </p>

      {/* Inline keyframes for the spinner animation */}
      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
};

/**
 * Page-specific loading fallback with context
 */
export const PageLoadingFallback = ({ pageName }) => {
  const { t } = useTranslation();
  return <LoadingFallback message={t("loading.page", "Loading {{pageName}}...", { pageName: pageName || t("loading.pageFallback", "page") })} />;
};

/**
 * Full-page loading overlay for initial app load
 */
export const FullPageLoader = () => {
  const { t } = useTranslation();
  return (
  <div
    style={{
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: "#ffffff",
      zIndex: 9999,
    }}
  >
    <LoadingFallback message={t("loading.compareMechanic", "Loading CompareMechanic...")} />
  </div>
  );
};

export default LoadingFallback;
